import { Component, Inject } from '@angular/core'
import {
    Action,
    arrayComparatorFactory,
    booleanComparator,
    compareDates,
    ConfirmationService,
    Entry,
    EnvironmentService,
    ErrorsT,
    FieldTracker,
    FluentTracker,
    HandbookService,
    im,
    isPositiveInt,
    isZeroOrPositiveInt,
    LabelProvider,
    libArrayValuesToMap,
    LibraryHelper,
    libValuesToMap,
    selfValuesFactory,
    SmartColumns,
    stringComparator,
    stringToMap,
    Table,
    TableControlType,
    TableDelegate,
    TableExportType,
    Via
} from '@surgetech-eu/core'
import { merge, Observable, of } from 'rxjs'

import moment from 'moment'
import uuid from 'uuid'
import { finalize, map, mergeMap, tap } from 'rxjs/operators'
import { CatalogueObjectType, CatalogueObjectTypeService } from '../services/catalogue-object-type.service'
import { NONE_VALUE, disabledColumnOptions, galvanicTreatmentOptions, maxRank } from '../constant'
import { MainPartService } from '../services/main-part.service'
import { ComponentTypeService } from '../services/component-type.service'
import { ComponentTypeModel, LibraryModel } from '../models'
import { isMainPartsEmpty, SortRank } from '../helpers'
import { ActivatedRoute } from '@angular/router'

export enum columnName {
    key = 'key',
    type = 'type',
    galvanicTreatment = 'galvanicTreatment',
    mainPart = 'mainParts',
    description = 'description',
    translationUs = 'translationUs',
    translationIt = 'translationIt',
    translationEn = 'translationEn',
    disabled = 'disabled',
    rank = 'rank',
    cotLimit = 'maxNumberForLinksPerEyewear'
}

export type ForControlCanShow = true | false | 'new'

export interface CreateControlParams {
    column: string
    type: TableControlType
    canShow?: ForControlCanShow
    values?: any
    makeString?: boolean
}

const isEmptyValue = v => v == null || (typeof v === 'string' && !v.trim())

@Component({
    selector: 'catalogue-object-type',
    templateUrl: './catalogue-object-type.component.html',
    styleUrls: ['./catalogue-object-type.component.scss'],
    standalone: false
})
@SmartColumns()
export class CatalogueObjectTypeComponent implements TableDelegate {
    private table: Table

    saving = false
    saved = false
    changed = false

    error: ErrorsT = null

    private fluentTracker: FluentTracker
    private pendingSource: CatalogueObjectType[] = []
    private originSource: CatalogueObjectType[] = []
    private typeSource: ComponentTypeModel[] = []
    private mainPartSource: LibraryModel[] = []
    private MainPartRowOptions = new Map<string, any>()
    private typeOptions = new Map<string, string>()
    private mainPartOptions = new Map<string, string>()
    private labels: LabelProvider

    importUrl = '/libraries/management/types/catalogObjectType/import-entries'
    private noErrorsInEdited: boolean

    constructor(
        @Inject('surge.core.schema-transformers') private schemaTransformers: any[],
        private catalogueObjectTypeService: CatalogueObjectTypeService,
        private mainPartService: MainPartService,
        private componentTypeService: ComponentTypeService,
        private handbookService: HandbookService,
        private environment: EnvironmentService,
        private confirmationService: ConfirmationService,
        private route: ActivatedRoute
    ) {
        this.labels = route.snapshot.data.labels
        this.fluentTracker = new FluentTracker(new FieldTracker(), {
            fields: {
                id: {
                    ns: true
                },
                key: {
                    required: true,
                    validate: (value, extra, withinNs) => {
                        if (!value) {
                            return "It's required"
                        }
                        if (
                            this.pendingSource.findIndex(
                                item =>
                                    item.key.trim().toLowerCase() === value.trim().toLowerCase() &&
                                    item.id.trim().toLowerCase() !== withinNs.trim().toLowerCase()
                            ) !== -1
                        ) {
                            return 'Should have a unique value'
                        }
                        return null
                    }
                },
                type: {
                    required: true,
                    validate: (v, extra, withinNs) => {
                        if (!v) {
                            return "It's required"
                        }
                        if (v && typeof v === 'string') {
                            const findT = this.typeSource.find(tItem => tItem.key?.toLowerCase() === v?.toLowerCase())
                            if (!this.typeOptions.get(v?.toLowerCase()) || (findT?.disabled && v)) {
                                return im('{value} is not found', { value: findT?.key + '-' + findT?.description })
                            }
                        }
                        if (v && typeof v === 'object') {
                            const findT = this.typeSource.find(
                                tItem => tItem.key?.toLowerCase() === v.id?.toLowerCase()
                            )
                            if (!this.typeOptions.get(v.id?.toLowerCase()) || (findT?.disabled && v)) {
                                return im('{value} is not found', {
                                    value: LibraryHelper.libraryAttributeToStringOption(v)
                                })
                            }
                        }
                        return null
                    }
                },
                galvanicTreatment: {
                    validate: (v, extra, withinNs) => {
                        const findElem = this.pendingSource.find(item => item.id === withinNs)
                        if (v && findElem && findElem.type?.id === 'RAWM') {
                            return 'It should be empty for raw material'
                        }
                        if (!v && this.pendingSource.find(item => item.id === withinNs)?.type?.id !== 'RAWM') {
                            return "It's required"
                        }
                        return null
                    },
                    required: false
                },
                mainParts: {
                    validate: (v, extra, withinNs) => {
                        const errors = []
                        const findElem = this.pendingSource.find(item => item.id === withinNs)
                        if (v?.length && findElem && findElem?.type?.id === 'RAWM') {
                            return 'It should be empty'
                        }
                        if (!v?.length && findElem && findElem?.type?.id !== 'RAWM') {
                            return "It's required"
                        }
                        if (v && typeof v[0] === 'string') {
                            if (v?.length && findElem && findElem?.type?.id !== 'RAWM') {
                                v.map(item => {
                                    const findMP = this.mainPartSource.find(
                                        mPItem => mPItem.key?.toLowerCase() === item?.toLowerCase()
                                    )
                                    if (!this.mainPartOptions.get(item?.toLowerCase()) || (findMP?.disabled && item)) {
                                        errors.push(findMP?.key + '-' + findMP?.description)
                                    }
                                })
                                if (errors.length) {
                                    errors.push('not found')
                                    return errors
                                }
                            }
                        }
                        if (v && typeof v[0] === 'object') {
                            if (v?.length && findElem && findElem?.type?.id !== 'RAWM') {
                                v.map(item => {
                                    const findMP = this.mainPartSource.find(
                                        mPItem => mPItem.key?.toLowerCase() === item.id?.toLowerCase()
                                    )
                                    if (
                                        !this.mainPartOptions.get(item.id.toLowerCase()) ||
                                        (findMP?.disabled && item.id)
                                    ) {
                                        errors.push(LibraryHelper.libraryAttributeToStringOption(item))
                                    }
                                })
                                if (errors.length) {
                                    errors.push('not found')
                                    return errors
                                }
                            }
                        }
                        return null
                    },
                    required: false
                },
                description: {
                    required: false
                },
                translationUs: {
                    required: false
                },
                translationIt: {
                    required: false
                },
                translationEn: {
                    required: false
                },
                disabled: {
                    required: false
                },
                rank: {
                    required: false,
                    validate: value => {
                        if (value === null) {
                            return null
                        }
                        if (!isPositiveInt(value)) {
                            return 'Should be a positive number'
                        }
                        if (value > maxRank) {
                            return im('It must be less than {maxRank}', { maxRank })
                        }
                        return null
                    }
                },
                maxNumberForLinksPerEyewear: {
                    required: false,
                    validate: value => {
                        if (isEmptyValue(value) || !isZeroOrPositiveInt(value)) {
                            return 'Should be zero or a positive number'
                        }
                        return null
                    },
                    validateIfEmpty: true
                },
                library: {
                    required: false
                },
                namespace: {
                    required: true
                },
                new: {
                    required: false
                },
                changed: {}
            }
        })
        this.fluentTracker.listen(
            FluentTracker.EVENT_CHANGE,
            () => {
                const data = this.fluentTracker.generate()

                this.noErrorsInEdited = data.every(
                    row => (!row.changed && !row.new) || !this.fluentTracker.hasErrors(row.id)
                )

                this.error = null
                this.table.refreshSizes()
            },
            true
        )
    }

    onTableDelegateBind(table: Table): void {
        this.table = table
    }

    disableSupportQuery(): boolean {
        return true
    }

    getColumnsConfig() {
        return [
            {
                name: columnName.key,
                path: 'key',
                title: this.labels.get('libraries.catalogObjectTypeTable.key'),
                type: 'text'
            },
            {
                name: columnName.description,
                path: 'description',
                title: this.labels.get('libraries.catalogObjectTypeTable.description'),
                type: 'text'
            },
            {
                name: columnName.translationUs,
                title: this.labels.get('libraries.catalogObjectTypeTable.translationUs'),
                type: 'text'
            },
            {
                name: columnName.translationIt,
                title: this.labels.get('libraries.catalogObjectTypeTable.translationIt'),
                type: 'text'
            },
            {
                name: columnName.translationEn,
                title: this.labels.get('libraries.catalogObjectTypeTable.translationEn'),
                type: 'text'
            },
            {
                name: columnName.type,
                title: this.labels.get('libraries.catalogObjectTypeTable.type'),
                type: 'text'
            },
            {
                name: columnName.galvanicTreatment,
                title: this.labels.get('libraries.catalogObjectTypeTable.galvanicTreatment'),
                type: 'text'
            },
            {
                name: columnName.mainPart,
                title: this.labels.get('libraries.catalogObjectTypeTable.mainParts')
            },
            {
                name: 'createdAt',
                title: this.labels.get('libraries.catalogObjectTypeTable.createdAt'),
                type: 'date'
            },
            {
                name: 'createdBy',
                title: this.labels.get('libraries.catalogObjectTypeTable.createdBy'),
                type: 'text'
            },
            {
                name: 'updatedAt',
                title: this.labels.get('libraries.catalogObjectTypeTable.updatedAt'),
                type: 'date'
            },
            {
                name: 'updatedBy',
                title: this.labels.get('libraries.catalogObjectTypeTable.updatedBy'),
                type: 'text'
            },
            {
                name: columnName.rank,
                path: 'rank',
                title: this.labels.get('libraries.catalogObjectTypeTable.rank')
            },
            {
                name: columnName.cotLimit,
                path: 'maxNumberForLinksPerEyewear',
                title: this.labels.get('libraries.componentTypeTable.cotLimit')
            },
            {
                name: columnName.disabled,
                title: this.labels.get('libraries.catalogObjectTypeTable.disabled')
            }
        ]
    }

    customizeColumns(columnsMap) {
        const keyColumn = columnsMap.get(columnName.key)
        keyColumn.control = this.getControlForType({
            column: columnName.key,
            type: TableControlType.INPUT,
            canShow: 'new'
        })
        keyColumn.filter = {
            via: Via.TEXT
        }

        const descriptionColumn = columnsMap.get(columnName.description)
        descriptionColumn.control = this.getControlForType({
            column: columnName.description,
            type: TableControlType.INPUT
        })
        descriptionColumn.filter = {
            via: Via.TEXT
        }

        const translationUsColumn = columnsMap.get(columnName.translationUs)
        translationUsColumn.control = this.getControlForType({
            column: columnName.translationUs,
            type: TableControlType.INPUT
        })
        translationUsColumn.filter = {
            via: Via.TEXT
        }

        const translationItColumn = columnsMap.get(columnName.translationIt)
        translationItColumn.control = this.getControlForType({
            column: columnName.translationIt,
            type: TableControlType.INPUT
        })
        translationItColumn.filter = {
            via: Via.TEXT
        }

        const translationEnColumn = columnsMap.get(columnName.translationEn)
        translationEnColumn.control = this.getControlForType({
            column: columnName.translationEn,
            type: TableControlType.INPUT
        })
        translationEnColumn.filter = {
            via: Via.TEXT
        }

        const typeColumn = columnsMap.get(columnName.type)
        typeColumn.filter = {
            via: Via.SELF,
            selfValues: selfValuesFactory('type', libValuesToMap),
            values: this.typeOptions,
            path: 'type.id'
        }
        typeColumn.control = {
            type: TableControlType.SELECT,
            values: (v, row) => {
                const options = new Map<string, string>()
                this.typeSource.sort(SortRank).map(item => {
                    if (!item.disabled || row.type?.id.toLowerCase() === item.key.toLowerCase()) {
                        options.set(
                            item.key.toLowerCase(),
                            item.description ? item.key + ' - ' + item.description : item.key
                        )
                    }
                })
                if (v && !this.typeOptions.get(v.id?.toLowerCase())) {
                    options.set(v.id.toLowerCase(), v.code ? v.id + ' - ' + v.code : v.id)
                }
                return options
            },
            change: (value, row) => {
                const find = this.typeSource.find(item => item.key.toLowerCase() === value.toLowerCase())
                if (find) {
                    value = {
                        id: find.key,
                        code: find.description
                    }
                }
                this.updateFluentValue(value, row, columnName.type)
                if (value.id === 'RAWM') {
                    this.updateValue(row.id, columnName.galvanicTreatment, null)
                    this.updateValue(row.id, columnName.mainPart, null)
                }
                if (
                    this.fluentTracker.tracker.hasError(row.id, columnName.mainPart) ||
                    this.fluentTracker.tracker.hasError(row.id, columnName.galvanicTreatment)
                ) {
                    this.fluentTracker.validate(row.id, columnName.galvanicTreatment)
                    this.fluentTracker.validate(row.id, columnName.mainPart)
                }
            },
            canShow: () => true,
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, columnName.type),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, columnName.type),
            seed: row => {
                return this.fluentTracker.tracker.getValue(row.id, columnName.type)?.id || row.type?.id
            }
        }

        const galvanicTreatmentColumn = columnsMap.get(columnName.galvanicTreatment)
        galvanicTreatmentColumn.control = {
            type: TableControlType.SELECT,
            values: galvanicTreatmentOptions,
            change: (value, row) => {
                this.updateFluentValue(value, row, columnName.galvanicTreatment)
            },
            canShow: (v, row) =>
                row.type?.id !== 'RAWM' || this.fluentTracker.tracker.hasError(row.id, columnName.galvanicTreatment),
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, columnName.galvanicTreatment),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, columnName.galvanicTreatment),
            seed: row => {
                return this.fluentTracker.tracker.getValue(row.id, columnName.galvanicTreatment) ||
                    typeof row.galvanicTreatment === 'boolean'
                    ? row.galvanicTreatment?.toString()
                    : null
            }
        }
        galvanicTreatmentColumn.filter = {
            via: Via.DROPDOWN,
            values: galvanicTreatmentOptions
        }
        galvanicTreatmentColumn.pipe = (value, row) => {
            if (row.type?.id === 'RAWM') {
                return null
            }
            return value ? 'Yes' : 'No'
        }

        const mainPartColumn = columnsMap.get(columnName.mainPart)
        mainPartColumn.filter = {
            via: Via.SELFPICKER,
            selfValues: selfValuesFactory('mainParts', libArrayValuesToMap),
            comparator: arrayComparatorFactory(v => (v ? v.map(item => item.id) : []))
        }
        mainPartColumn.control = {
            type: TableControlType.SELECT,
            values: (v, row) => {
                const options = new Map<string, string>()
                this.mainPartSource.sort(SortRank).map(item => {
                    if (
                        !item.disabled ||
                        row.mainParts?.find(findItem => findItem.id.toLowerCase() === item.key.toLowerCase())
                    ) {
                        options.set(
                            item.key.toLowerCase(),
                            item.description ? item.key + ' - ' + item.description : item.key
                        )
                    }
                })
                v?.forEach(item => {
                    if (!this.mainPartOptions.get(item.id.toLowerCase())) {
                        options.set(item.id.toLowerCase(), item.code ? item.id + ' - ' + item.code : item.id)
                    }
                })
                this.MainPartRowOptions.set(row.id, options)
                return options
            },
            multiple: true,
            change: (value, row) => {
                if (value?.length) {
                    value = value
                        .map(itemId => {
                            const find = this.mainPartSource.find(
                                item => item.key.toLowerCase() === itemId.toLowerCase()
                            )
                            if (find) {
                                return {
                                    id: find.key,
                                    code: find.description
                                }
                            } else {
                                return {
                                    id: row.mainParts.find(item => item.id.toLowerCase() === itemId.toLowerCase()).id,
                                    code: row.mainParts.find(item => item.id.toLowerCase() === itemId.toLowerCase())
                                        .code
                                }
                            }
                        })
                        .filter(item => item)
                } else {
                    value = null
                }
                this.updateFluentValue(value, row, columnName.mainPart)
            },
            canShow: (v, row) =>
                row.type?.id !== 'RAWM' || this.fluentTracker.tracker.hasError(row.id, columnName.mainPart),
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, columnName.mainPart),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, columnName.mainPart),
            seed: row => {
                return (
                    this.fluentTracker.tracker
                        .getValue(row.id, columnName.mainPart)
                        ?.map(item => (item.id ? item.id?.toLowerCase() : item)) ||
                    row.mainParts?.map(item => item.id?.toLowerCase())
                )
            }
        }

        mainPartColumn.pipe = (value, row) => {
            if (row.type?.id === 'RAWM') {
                return null
            }
            return value
        }

        const createdAtColumn = columnsMap.get('createdAt')
        //         createdAtColumn.pipe = v => (v ? moment(v).format(DD/MM/YYYY) : )
        createdAtColumn.filter = {
            via: Via.MOMENT
        }
        createdAtColumn.sorting = {
            sortable: true,
            compare: compareDates('createdAt')
        }

        const createdByColumn = columnsMap.get('createdBy')
        createdByColumn.filter = {
            via: Via.SELF,
            selfValues: selfValuesFactory('createdBy', stringToMap),
            path: 'createdBy'
        }

        const updatedAtColumn = columnsMap.get('updatedAt')
        //         updatedAtColumn.pipe = v => (v ? moment(v).format(DD/MM/YYYY) : )
        updatedAtColumn.filter = {
            via: Via.MOMENT
        }
        updatedAtColumn.sorting = {
            sortable: true,
            compare: compareDates('updatedAt')
        }

        const updatedByColumn = columnsMap.get('updatedBy')
        updatedByColumn.filter = {
            via: Via.SELF,
            selfValues: selfValuesFactory('updatedBy', stringToMap),
            path: 'updatedBy'
        }

        const rankColumn = columnsMap.get(columnName.rank)
        rankColumn.control = this.getControlForType({ column: columnName.rank, type: TableControlType.INPUT })
        rankColumn.sorting = {
            sortable: true
        }
        rankColumn.filter = {
            via: Via.TEXT
        }

        const cotLimitColumn = columnsMap.get(columnName.cotLimit)
        cotLimitColumn.control = {
            type: TableControlType.INPUT,
            changing: (value, row) => {
                this.updateFluentValue(value.trim(), row, columnName.cotLimit)
            },
            disabled: () => false,
            canShow: (v, row) => {
                const originValue = this.originSource.find(({ id }) => id === row.id) || {}
                return originValue[columnName.cotLimit] ? !Number(originValue[columnName.cotLimit]) : true
            },
            seed: row =>
                this.fluentTracker.tracker.getValue(row.id, columnName.cotLimit) ||
                (isEmptyValue(row[columnName.cotLimit]) ? '' : row[columnName.cotLimit].toString())
        }
        cotLimitColumn.sorting = {
            sortable: true
        }
        cotLimitColumn.filter = {
            via: Via.TEXT
        }
        cotLimitColumn.error = {
            has: row => this.fluentTracker.tracker.hasError(row.id, columnName.cotLimit),
            get: row => this.fluentTracker.tracker.getError(row.id, columnName.cotLimit)
        }

        const disableColumn = columnsMap.get(columnName.disabled)
        disableColumn.control = {
            type: TableControlType.DROPDOWN,
            values: disabledColumnOptions,
            required: true,
            change: (value, row) => {
                this.updateValue(row.id, columnName.disabled, value)
                this.updateExistFields(row)
            },
            disabled: () => false,
            canShow: () => true,
            seed: row => this.fluentTracker.tracker.getValue(row.id, columnName.disabled) || row.disabled?.toString()
        }
        disableColumn.sorting = {
            sortable: false
        }
        disableColumn.filter = {
            via: Via.DROPDOWN,
            values: disabledColumnOptions,
            comparator: booleanComparator
        }
    }

    canTableRemove(): boolean {
        return true
    }

    canTableExport(): boolean {
        return true
    }

    getTableExportTypes(): TableExportType[] {
        return [
            {
                name: 'xlsx',
                url: '/libraries/management/types/catalogObjectType/export-entries/xlsx'
            }
        ]
    }

    mapBeforeTableExportDirectly(rows: any[]): any[] {
        this.fluentTracker.validateAll()
        if (!this.fluentTracker.hasErrors()) {
            return rows.map(item => {
                delete item.__index
                delete item.__internalRowNumbers
                delete item.__sortIndex
                delete item.new
                return item
            })
        } else {
            return rows
                .map(item => {
                    delete item.__index
                    delete item.__internalRowNumbers
                    delete item.__sortIndex
                    return item
                })
                .filter(item => !item.new)
        }
    }

    generateTableExportFilename(type: TableExportType): string {
        return 'BC-' + moment().format('YYYY-MM-DD') + '-Handbook-catalogObjectType.xlsx'
    }

    get importTemplateUrl() {
        return '/libraries/management/types/catalogObjectType/import-entries/template.xlsx'
    }

    shouldTableExportDirectly() {
        return true
    }

    importFailed(error) {
        if (typeof error === 'string') {
            this.error = error
        } else {
            this.error = 'Unknown error'
        }
    }

    beforeImport() {
        this.error = null
        this.saved = false
    }

    afterImport(imported) {
        if (imported.length === 0) {
            return
        }

        const entriesByKey: { [key: string]: Entry } = {}
        this.table.getAllData().forEach(row => (entriesByKey[row.key] = row))
        let hasDuplicates = false
        let hasRankOverflow = false
        let hasNotIntRank = false
        let hasNotIntCotLimit = false
        imported.forEach(entry => {
            const found = entriesByKey[entry.key]
            if (found) {
                hasDuplicates = true
            }
            if (entry.rank && !isZeroOrPositiveInt(entry.rank)) {
                hasNotIntRank = true
            }
            if (entry[columnName.cotLimit] && !isZeroOrPositiveInt(entry[columnName.cotLimit])) {
                hasNotIntCotLimit = true
            }
            if (entry.rank > maxRank) {
                hasRankOverflow = true
            }
            this.validateSetAfterImportFields(entry)
        })
        const error = []
        if (hasDuplicates) {
            error.push('The key must be unique.')
        }
        if (hasNotIntRank || hasRankOverflow) {
            error.push(im('Rank should be a positive number and less than {maxRank}.', { maxRank }))
        }
        if (hasNotIntCotLimit) {
            error.push(`COT limit should be a positive number.`)
        }
        if (error?.length) {
            error.push('Please check the data.')
        }
        if (this.fluentTracker.hasErrors()) {
            if (!error?.length) {
                error.push('Some of the imported entries have errors. Please check the data.')
            }
            this.error = error
        }
        this.table.goToLastPage()
        this.table.reload()
    }

    validateSetAfterImportFields(entry): any {
        const newItem = Object.assign({}, this.createNewCatalogueObjectType(), entry)
        this.pendingSource.push(newItem)
        Object.keys(newItem).forEach(field => {
            if (field === 'disabled') {
                this.updateValue(newItem.id, field, newItem[field]?.toString())
                return
            }
            if (field === 'mainParts') {
                if (newItem[field]?.length) {
                    newItem[field] = newItem[field].map(item => {
                        const findMpItem = this.mainPartSource.find(
                            mPItem => mPItem.key?.toLowerCase() === item.id?.toLowerCase()
                        )
                        if (findMpItem) {
                            return {
                                id: findMpItem?.key,
                                code: findMpItem?.description
                            }
                        }
                        return item
                    })
                }
                this.updateValue(newItem.id, columnName.mainPart, newItem[field])
                if (!isMainPartsEmpty(newItem.mainParts) && newItem.type?.id !== 'RAWM') {
                    const errors = []
                    newItem.mainParts.forEach(item => {
                        const findMP = this.mainPartSource.find(
                            mPItem => mPItem.key?.toLowerCase() === item.id?.toLowerCase()
                        )
                        if (!this.mainPartOptions.get(item.id?.toLowerCase()) || (findMP?.disabled && item.id)) {
                            if (findMP) {
                                errors.push(findMP.description ? findMP.key + '-' + findMP.description : findMP.key)
                            } else {
                                errors.push(item.code ? item.id + '-' + item.code : item.id)
                            }
                        }
                    })
                    if (errors.length) {
                        errors.push('not found')
                        this.fluentTracker.tracker.addError(newItem.id, columnName.mainPart, errors)
                    }
                } else if (isMainPartsEmpty(newItem.mainParts) && newItem.type?.id !== 'RAWM') {
                    this.fluentTracker.tracker.addError(newItem.id, columnName.mainPart, "It's required")
                }
                if (!isMainPartsEmpty(newItem.mainParts) && newItem.type?.id === 'RAWM') {
                    this.fluentTracker.tracker.addError(newItem.id, columnName.mainPart, 'It should be empty')
                }

                return
            }
            if (field === 'type') {
                const findT = this.typeSource.find(
                    tItem => tItem.key?.toLowerCase() === newItem.type?.id?.toLowerCase()
                )
                let type = null
                if (findT) {
                    type = { id: findT.key, code: findT.description }
                }
                this.updateValue(newItem.id, columnName.type, type || newItem.type)
                if (!this.typeOptions.get(newItem[field].id?.toLowerCase()) || findT?.disabled) {
                    if (findT) {
                        this.fluentTracker.tracker.addError(
                            newItem.id,
                            columnName.type,
                            findT.description
                                ? im('{value} is not found', { value: findT.key + '-' + findT.description })
                                : im('{value} is not found', { value: findT.key })
                        )
                    } else {
                        this.fluentTracker.tracker.addError(
                            newItem.id,
                            columnName.type,
                            im('{value} is not found', {
                                value: LibraryHelper.libraryAttributeToStringOption(newItem[field])
                            })
                        )
                    }
                } else if (!newItem[field]?.id) {
                    this.fluentTracker.tracker.addError(newItem.id, columnName.type, "It's required")
                }

                return
            }
            if (field === 'galvanicTreatment') {
                this.updateValue(newItem.id, columnName.galvanicTreatment, newItem[field]?.toString())
                if (typeof newItem[field] === 'boolean' && newItem.type?.id === 'RAWM') {
                    this.fluentTracker.tracker.addError(
                        newItem.id,
                        columnName.galvanicTreatment,
                        'it should be empty for raw material'
                    )
                } else if (typeof newItem[field] !== 'boolean' && newItem.type?.id !== 'RAWM') {
                    this.fluentTracker.tracker.addError(newItem.id, columnName.galvanicTreatment, "It's required")
                }
                return
            }
            this.updateValue(newItem.id, field, newItem[field])
        })
        this.updateExistFields(newItem)
    }

    configureTableRemoveAction(action: Action) {
        action.disabled = (row: CatalogueObjectType) => row.isUsed
        action.run = (row: CatalogueObjectType) => {
            return new Observable(observer => {
                const confirmation = this.confirmationService.openWithParams({
                    text: 'Are you sure that you want to delete this entry?',
                    title: 'Delete'
                })
                confirmation
                    .then(() => {
                        this.error = null
                        this.saved = false

                        if (row.new) {
                            this.removeItem(row.id)
                            return of(true)
                        }
                        delete row.__index
                        delete row.__internalRowNumbers
                        delete row.__sortIndex
                        return this.catalogueObjectTypeService.remove([row]).subscribe(
                            data => {
                                this.removeItem(row.id)
                                observer.next(data)
                            },
                            error => {
                                this.error = error
                            }
                        )
                    })
                    .catch(() => {
                        observer.next(true)
                    })
            })
        }
    }

    getTableData(): Observable<CatalogueObjectType[]> {
        if (this.pendingSource.length) {
            return of(this.pendingSource)
        }
        return this.mainPartService.getAll().pipe(
            mergeMap(mainParts => {
                this.mainPartSource = mainParts
                mainParts.forEach(item => {
                    this.mainPartOptions.set(
                        item.key.toLowerCase(),
                        item.description ? item.key + ' - ' + item.description : item.key
                    )
                })
                return this.componentTypeService.getAll()
            }),
            mergeMap(componentType => {
                this.typeSource = componentType.filter(type => !type.disabled)
                this.typeSource.forEach(item => {
                    this.typeOptions.set(
                        item.key.toLowerCase(),
                        item.description ? item.key + ' - ' + item.description : item.key
                    )
                })
                return this.catalogueObjectTypeService.getAll()
            }),
            map(data =>
                data.map(catalogueObjectType => {
                    return {
                        ...catalogueObjectType
                    }
                })
            ),
            tap(data => {
                this.pendingSource = [...data]
                this.originSource = JSON.parse(JSON.stringify(data))
                this.fillTracker()
            })
        )
    }

    canSave(): boolean {
        return this.changed && this.noErrorsInEdited && !this.saving
    }

    private fieldValidation(): void {
        const keys = this.fluentTracker.generate()
        const result = keys.reduce(
            (acc, key) => {
                if (key.type?.id !== 'RAWM' && !key.galvanicTreatment) {
                    acc.requiredGT.push(key)
                }
                if (key.type?.id !== 'RAWM' && !key.mainParts?.length) {
                    acc.requiredMP.push(key)
                }
                if (key.galvanicTreatment && !galvanicTreatmentOptions.get(key.galvanicTreatment)) {
                    acc.notFoundGT.push(key)
                }

                return acc
            },
            {
                requiredGT: [],
                requiredMP: [],
                notFoundGT: []
            }
        )
        if (result.requiredGT) {
            result.requiredGT.forEach(item => {
                this.fluentTracker.tracker.addError(item.id, columnName.galvanicTreatment, "It's required")
            })
        }
        if (result.requiredMP) {
            result.requiredMP.forEach(item => {
                this.fluentTracker.tracker.addError(item.id, columnName.mainPart, "It's required")
            })
        }
        if (result.notFoundGT) {
            result.notFoundGT.forEach(item => {
                this.fluentTracker.tracker.addError(item.id, columnName.galvanicTreatment, 'not found')
            })
        }
    }

    private convertPayload(): { create?: CatalogueObjectType[]; update?: CatalogueObjectType[] } | [] {
        this.fieldValidation()
        const fluentTracker = this.fluentTracker.generate()
        const editedItems = fluentTracker.filter(item => item.changed || item.new)

        const data = {
            create: [],
            update: []
        }
        editedItems.forEach(item => {
            item.galvanicTreatment = item.galvanicTreatment === 'true'
            if (item.type?.id === 'RAWM') {
                item.galvanicTreatment = null
                item.mainParts = null
            }
            item.disabled = item.disabled === 'true'
            item.type = this.typeSource.find(typeItem => typeItem.key.toLowerCase() === item.type?.id.toLowerCase())
            if (item.type) {
                item.type = {
                    id: item.type.key,
                    code: item.type.description
                }
            }
            const originValue = this.originSource.find(({ id }) => id === item.id) || {}
            if (Number(originValue[columnName.cotLimit] || 0) === NONE_VALUE) {
                item[columnName.cotLimit] = Number(item[columnName.cotLimit] || 0)
            } else {
                delete item[columnName.cotLimit]
            }
            delete item.changed
            if (item.new) {
                delete item.new
                data.create.push({ ...item })
            } else {
                data.update.push({ ...item })
            }
        })
        return data
    }

    save(): void {
        const data = this.convertPayload()
        this.error = null
        this.saved = false
        if (!Array.isArray(data)) {
            const { create, update } = data

            const requests = []

            if (create.length) {
                requests.push(this.catalogueObjectTypeService.create(create))
            }

            if (update.length) {
                requests.push(this.catalogueObjectTypeService.update(update))
            }

            this.saving = true

            merge(...requests)
                .pipe(
                    finalize(() => {
                        this.pendingSource = []
                        this.fluentTracker.clearAll()
                        this.saving = false
                        this.table.reload()
                    })
                )
                .subscribe(
                    () => {
                        this.saved = true
                    },
                    error => {
                        const arrayError = []
                        if (typeof error === 'object') {
                            Object.entries(error).forEach(([key, value]) => {
                                if (arrayError.length > 0) {
                                    arrayError.push(' , ')
                                }
                                arrayError.push(value)
                            })
                            this.error = arrayError.join('')
                            return
                        }
                        this.error = error
                    }
                )
        }
    }

    private createNewCatalogueObjectType(): CatalogueObjectType {
        const id = uuid()
        return {
            key: '',
            description: '',
            translationUs: '',
            translationIt: '',
            translationEn: '',
            galvanicTreatment: null,
            type: null,
            mainParts: null,
            disabled: false,
            rank: null,
            maxNumberForLinksPerEyewear: '0',
            id,
            new: true,
            library: 'catalogObjectType',
            namespace: 'kering'
        }
    }

    addNewItem(): void {
        const newItem = this.createNewCatalogueObjectType()
        this.pendingSource.splice(this.pendingSource.length + 1, 0, newItem)
        this.updateExistFields(newItem)
        this.table.reload()
        this.table.goToLastPage()
    }

    private removeItem(id: string) {
        this.fluentTracker.tracker.destroy(id)
        const removedIndex = this.pendingSource.findIndex(md => md.id === id)
        this.pendingSource.splice(removedIndex, 1)
        this.table.reload()
    }

    private updateExistFields(row: CatalogueObjectType): void {
        if (!this.fluentTracker.tracker.hasValue(row.id, columnName.key) && row.key) {
            this.updateValue(row.id, columnName.key, row.key)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, 'library')) {
            this.updateValue(row.id, 'library', row.library)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, 'namespace')) {
            this.updateValue(row.id, 'namespace', row.namespace)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, columnName.type) && row.type) {
            this.updateValue(row.id, columnName.type, row.type)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, columnName.description) && row.description) {
            this.updateValue(row.id, columnName.description, row.description)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, columnName.translationUs)) {
            this.updateValue(row.id, columnName.translationUs, row.translationUs)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, columnName.translationIt)) {
            this.updateValue(row.id, columnName.translationIt, row.translationIt)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, columnName.translationEn)) {
            this.updateValue(row.id, columnName.translationEn, row.translationEn)
        }
        if (
            !this.fluentTracker.tracker.hasValue(row.id, columnName.galvanicTreatment) &&
            row.type?.id !== 'RAWM' &&
            row.galvanicTreatment?.toString()
        ) {
            this.updateValue(row.id, columnName.galvanicTreatment, row.galvanicTreatment?.toString())
        }
        if (
            !this.fluentTracker.tracker.hasValue(row.id, columnName.mainPart) &&
            row.type?.id !== 'RAWM' &&
            row.mainParts?.length
        ) {
            this.updateValue(
                row.id,
                columnName.mainPart,
                row.mainParts?.map(item => item.id)
            )
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, columnName.disabled)) {
            this.updateValue(row.id, columnName.disabled, row.disabled?.toString())
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, columnName.rank) && row.rank) {
            this.updateValue(row.id, columnName.rank, row.rank)
        }
        if (
            !this.fluentTracker.tracker.hasValue(row.id, columnName.cotLimit) &&
            !isEmptyValue(row[columnName.cotLimit])
        ) {
            this.updateValue(row.id, columnName.cotLimit, row[columnName.cotLimit])
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, 'new')) {
            this.updateValue(row.id, 'new', row.new)
        }
    }

    private updateFluentValue(value, row, column: string) {
        const indexToUpdate = this.pendingSource.findIndex(mc => mc.id === row.id)
        if (indexToUpdate !== -1) {
            this.pendingSource[indexToUpdate][column] = value
            row[column] = value
        }
        this.updateValue(row.id, column, value)
        this.updateExistFields(row)
    }

    getControlForType(params: CreateControlParams) {
        const { makeString = false, column, values, canShow = true, type } = params
        const newField = canShow === 'new'
        switch (type) {
            case TableControlType.INPUT: {
                return {
                    type: type,
                    changing: (value, row) => this.updateFluentValue(value.trim(), row, column),
                    canShow: (v, row) => {
                        if (newField) {
                            return row.new
                        }
                        if (this.fluentTracker.tracker.hasError(row.id, column)) {
                            return true
                        }
                        return canShow
                    },
                    failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, column),
                    failure: (v, row) => this.fluentTracker.tracker.getError(row.id, column),
                    seed: row => {
                        return this.fluentTracker.tracker.getValue(row.id, column) || row[column]
                    }
                }
            }
            case TableControlType.DROPDOWN: {
                return {
                    type: type,
                    values: values,
                    required: true,
                    change: (value, row) => this.updateFluentValue(value, row, column),
                    disabled: () => false,
                    canShow: (value, row) => {
                        if (newField) {
                            return row.new
                        }
                        if (this.fluentTracker.tracker.hasError(row.id, column)) {
                            return true
                        }
                        return canShow
                    },
                    seed: row =>
                        this.fluentTracker.tracker.getValue(row.id, column) || makeString
                            ? row[column].toString()
                            : row[column]
                }
            }
        }
    }

    private fillTracker() {
        const fields = Object.keys(this.fluentTracker.trackable.fields)
        this.pendingSource.forEach((row, i) => {
            fields.forEach(f => {
                let value = row[f]
                if (typeof value === 'boolean') {
                    value = value?.toString()
                }
                this.fluentTracker.update(row.id, f, value, { silent: true })
            })
        })

        this.fluentTracker.validateAll()
    }

    private updateValue(id, field, value) {
        this.fluentTracker.update(id, field, value)
        this.fluentTracker.update(id, 'changed', true, { silent: true })
        this.changed = true
    }
}
